/**
 * Direct Cognito authentication for the fan client.
 * React app talks to Amazon Cognito User Pool for sign-in/sign-up/password reset.
 * No custom fan-auth API - auth is handled entirely by Cognito.
 *
 * Prerequisites:
 * - Cognito App Client must have "ALLOW_USER_PASSWORD_AUTH" enabled (for login)
 * - Or use "ALLOW_USER_SRP_AUTH" for SRP flow (more secure, no password in request)
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";

const getConfig = () => {
  const appConfig = (window as any).appConfig || {};
  const userPoolId = appConfig.cognitoUserPoolId || "";
  const clientId = appConfig.cognitoClientId || "";
  if (!userPoolId || !clientId) {
    throw new Error("Cognito config missing: cognitoUserPoolId and cognitoClientId required in appConfig");
  }
  return { userPoolId, clientId };
};

const getCognitoUserPool = (): CognitoUserPool => {
  const { userPoolId, clientId } = getConfig();
  return new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });
};

export interface CognitoAuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export const cognitoLogin = (email: string, password: string): Promise<CognitoAuthResult> => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email.trim().toLowerCase(),
      Password: password,
    });

    const userPool = getCognitoUserPool();
    const cognitoUser = new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        resolve({
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          expiresIn: session.getAccessToken().getExpiration(),
        });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: () => {
        reject(new Error("New password required - please complete sign-up via the link sent to your email."));
      },
    });
  });
};

export interface CognitoSignUpResult {
  userSub: string;
  userConfirmed: boolean;
}

export const cognitoSignUp = (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
): Promise<CognitoSignUpResult> => {
  return new Promise((resolve, reject) => {
    const userPool = getCognitoUserPool();
    const attributes = [
      new CognitoUserAttribute({ Name: "email", Value: email.trim().toLowerCase() }),
      new CognitoUserAttribute({ Name: "given_name", Value: firstName || "" }),
      new CognitoUserAttribute({ Name: "family_name", Value: lastName || "" }),
    ];

    userPool.signUp(
      email.trim().toLowerCase(),
      password,
      attributes,
      [],
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        if (!result) {
          reject(new Error("Sign up failed"));
          return;
        }
        resolve({
          userSub: result.userSub,
          userConfirmed: result.userConfirmed,
        });
      },
    );
  });
};

export const cognitoForgotPassword = (email: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const userPool = getCognitoUserPool();
    const cognitoUser = new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
};

export const cognitoConfirmPassword = (
  email: string,
  verificationCode: string,
  newPassword: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const userPool = getCognitoUserPool();
    const cognitoUser = new CognitoUser({
      Username: email.trim().toLowerCase(),
      Pool: userPool,
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
};

export const getCurrentCognitoUser = (): CognitoUser | null => {
  const userPool = getCognitoUserPool();
  return userPool.getCurrentUser();
};

export const cognitoGetSession = (): Promise<CognitoAuthResult> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentCognitoUser();
    if (!cognitoUser) {
      reject(new Error("No authenticated user"));
      return;
    }

    cognitoUser.getSession((err: Error | null, session: any) => {
      if (err) {
        reject(err);
        return;
      }
      if (!session?.isValid?.()) {
        reject(new Error("Session expired"));
        return;
      }
      resolve({
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        expiresIn: session.getAccessToken().getExpiration(),
      });
    });
  });
};

export const cognitoSignOut = (): void => {
  const cognitoUser = getCurrentCognitoUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};
